import { type NormalizedCacheObject, ApolloClient, from } from '@apollo/client'
import merge from 'deepmerge'
import { type IncomingHttpHeaders } from 'http'
import isEqual from 'lodash/isEqual'
import { type NextApiRequestCookies } from 'next/dist/server/api-utils'
import { useMemo } from 'react'

import { CookieRegistry } from '@/constants/cookieRegistry'
import { type LanguageKey, getLocale } from '@/domain/locale'
import { getCookieByKey } from '@/utils/cookies/getCookieByKey'
import { isServer } from '@/utils/isSSR'

import { initializeCache } from './cache'
import contextLink from './middlewares/contextLink'
import errorLink from './middlewares/errorLink'
import logger from './middlewares/logger'
import uploadLink from './middlewares/uploadLink'

export type RequestHeaders = {
  headers?: IncomingHttpHeaders
  cookies?: NextApiRequestCookies
}

/** Re-write initialize apollo */

export const APOLLO_STATE_PROP_NAME = '__APOLLO_STATE__'

let apolloClient: ApolloClient<NormalizedCacheObject> | null

const createApolloClient = ({
  languageKey,
  requestHeaders,
}: {
  languageKey: LanguageKey
  requestHeaders?: RequestHeaders
}) => {
  const isDevMode = !isServer() && process.env.NODE_ENV !== 'production'

  const cache = initializeCache(requestHeaders?.cookies)

  return new ApolloClient({
    cache,
    connectToDevTools: isDevMode,
    link: from([
      contextLink(languageKey, requestHeaders),
      errorLink,
      ...logger(),
      uploadLink({}, isServer()),
    ]),
    queryDeduplication: true,
    ssrMode: isServer(),
  })
}

export const initializeApollo = (props?: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  initialState?: any
  /** Server side requests to manually add headers that are automatically added on the client-side */
  requestHeaders?: RequestHeaders
}) => {
  const { initialState, requestHeaders } = props || {}

  const cookieLocale = isServer()
    ? requestHeaders?.cookies?.[CookieRegistry.SITE_LOCALE]
    : getCookieByKey(document.cookie, CookieRegistry.SITE_LOCALE)
  const { languageKey } = getLocale(cookieLocale)

  const _apolloClient = apolloClient ?? createApolloClient({ languageKey, requestHeaders })

  if (initialState) {
    const existingCache = _apolloClient.cache.extract()

    const data = merge(existingCache, initialState, {
      arrayMerge: (destinationArray, sourceArray) => [
        ...sourceArray,
        ...destinationArray.filter((d) => sourceArray.every((s) => !isEqual(d, s))),
      ],
    })

    _apolloClient.cache.restore(data)
  }

  if (isServer()) {
    return _apolloClient
  }

  if (!apolloClient) {
    apolloClient = _apolloClient
  }

  return _apolloClient
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const addApolloState = (client: ApolloClient<NormalizedCacheObject>, pageProps?: any) => {
  return { ...(pageProps || {}), [APOLLO_STATE_PROP_NAME]: client.cache.extract() }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const useApollo = (pageProps?: any) => {
  const state = pageProps?.[APOLLO_STATE_PROP_NAME]
  const client = useMemo(() => initializeApollo({ initialState: state }), [state])

  return client
}
